import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getClearStateAffiliate, initCheckAffiliate } from "../redux/actions";
import { useNavigate } from "react-router-dom";

const EMPTY_CODE_MESSAGE = "Please enter your affiliate code.";
const NO_ACTIVE_CODE_MESSAGE =
  "No active code exists, please reach out to your organization’s administrator or Career Scoops Support or select a new plan";

const useAffiliateCodeModal = (user) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { affilliateSuccess, affilliateError, affilliateLoading } = useSelector(
    (state) => state.scenariosReducer,
  );
  const [isOpened, setIsOpened] = useState(false);
  const [choosePlanFirstOpen, setChoosePlanFirstOpen] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    dispatch(getClearStateAffiliate());
    setError("");
    setCode("");
  }, []);

  useEffect(() => {
    if (affilliateError) {
      setError(NO_ACTIVE_CODE_MESSAGE);
    }
  }, [affilliateError]);

  const openAffiliateCodeModal = useCallback(() => {
    const isMentor = user?.role === "Mentor";
    // Students must pick a plan first; never prompt for affiliate code until they have one.
    if (user && !isMentor && user?.pricing_plan == null) {
      setChoosePlanFirstOpen(true);
      return;
    }
    if (user && user?.affiliate_active) {
      navigate("/pricingplan");
    } else {
      setCode("");
      setError("");
      setIsOpened(true);
    }
  }, [
    user?.role,
    user?.pricing_plan,
    user?.affiliate_active,
    user?.affiliate_code,
    navigate,
  ]);

  const closeChoosePlanFirstModal = useCallback(() => {
    setChoosePlanFirstOpen(false);
  }, []);

  const confirmChoosePlanFirstModal = useCallback(() => {
    setChoosePlanFirstOpen(false);
    navigate("/pricingplan");
  }, [navigate]);

  const closeAffiliateCodeModal = useCallback(() => {
    setIsOpened(false);
    setError("");
  }, []);

  const submitAffiliateCode = useCallback(async () => {
    if (affilliateLoading) return;

    const affiliateCode = code.trim();

    if (!affiliateCode) {
      setError(EMPTY_CODE_MESSAGE);
      return;
    }

    setError("");
    dispatch(initCheckAffiliate({ action: "save", code: affiliateCode }));
  }, [code, dispatch, affilliateLoading]);
  const confirmAffiliateCodeSuccess = useCallback(() => {
    setIsOpened(false);
    navigate("/scenario/0");
  }, [navigate]);
  const affiliateCodeModal = useMemo(
    () => ({
      code,
      error,
      isOpened,
      isSaving: affilliateLoading,
      isSuccess: affilliateSuccess,
    }),
    [code, error, isOpened, affilliateLoading, affilliateSuccess],
  );

  return {
    affiliateCodeModal,
    choosePlanFirstOpen,
    closeAffiliateCodeModal,
    closeChoosePlanFirstModal,
    confirmChoosePlanFirstModal,
    openAffiliateCodeModal,
    setAffiliateCode: setCode,
    submitAffiliateCode,
    confirmAffiliateCodeSuccess,
  };
};

export default useAffiliateCodeModal;
